import CloseIcon from '@mui/icons-material/Close';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MONTHS } from '../../constants/months';
import { useColors } from '../../hooks/useColors';

const MonthYearGroupedFullScreenDialog = ({
  isOpen,
  onClose,
  availableDates,
  selectedMonth,
  selectedYear,
  onMonthYearChange,
  onNext,
  disabled = false,
  isLoading = false,
  error = null,
}) => {
  const { background } = useColors();
  const [tempSelectedMonth, setTempSelectedMonth] = useState(selectedMonth);
  const [tempSelectedYear, setTempSelectedYear] = useState(selectedYear);
  const [open, setOpen] = useState(false);

  const getDisplayText = useCallback(() => {
    if (selectedMonth && selectedYear) {
      const month = MONTHS.find((m) => m.value === selectedMonth);
      return month ? `${month.label} ${selectedYear}` : 'Selecciona mes y año';
    }
    return 'Selecciona mes y año';
  }, [selectedMonth, selectedYear]);

  const handleClickOpen = useCallback(() => {
    if (!disabled) {
      setOpen(true);
    }
  }, [disabled]);

  const handleClose = useCallback(() => {
    setTempSelectedMonth(selectedMonth);
    setTempSelectedYear(selectedYear);
    setOpen(false);
    onClose();
  }, [selectedMonth, selectedYear, onClose]);

  const groupedOptions = useMemo(() => {
    if (!availableDates || typeof availableDates !== 'object') {
      return [];
    }

    const options = [];

    const sortedYears = Object.keys(availableDates)
      .map((year) => parseInt(year))
      .sort((a, b) => a - b);

    sortedYears.forEach((year) => {
      const monthsForYear = availableDates[year];

      if (monthsForYear && monthsForYear.length > 0) {
        monthsForYear.forEach((monthValue) => {
          const month = MONTHS.find((m) => m.value === monthValue);
          if (month) {
            options.push({
              value: `${monthValue}-${year}`,
              label: `${month.label} ${year}`,
              month: monthValue,
              year: year,
              group: year.toString(),
            });
          }
        });
      }
    });

    return options;
  }, [availableDates]);

  useEffect(() => {
    if (isOpen) {
      setTempSelectedMonth(selectedMonth);
      setTempSelectedYear(selectedYear);
    }
  }, [isOpen, selectedMonth, selectedYear]);

  const handleOptionSelect = useCallback((option) => {
    setTempSelectedMonth(option.month);
    setTempSelectedYear(option.year);
  }, []);

  const handleConfirm = useCallback(() => {
    if (tempSelectedMonth && tempSelectedYear) {
      onMonthYearChange(tempSelectedMonth, tempSelectedYear);
    }
    setOpen(false);
    onClose();
    
    if (onNext) {
      setTimeout(() => {
        onNext();
      }, 100);
    }
  }, [tempSelectedMonth, tempSelectedYear, onMonthYearChange, onClose, onNext]);

  const groupedByYear = useMemo(() => {
    const grouped = {};
    groupedOptions.forEach((option) => {
      if (!grouped[option.group]) {
        grouped[option.group] = [];
      }
      grouped[option.group].push(option);
    });
    return grouped;
  }, [groupedOptions]);

  const hasNoDates = !availableDates || Object.keys(availableDates).length === 0;
  const isConfirmDisabled = !tempSelectedMonth || !tempSelectedYear || isLoading || disabled;

  useEffect(() => {
    if (isOpen) {
      setOpen(true);
    }
  }, [isOpen]);

  return (
    <>
      <div>
        <h3 style={{ fontSize: 12, color: '#4f4f4f', marginBottom: '8px' }}>¿Cuándo viajas?</h3>
        <div
          onClick={handleClickOpen}
          className={`full-screen-dialog ${
            disabled ? 'disabled' : ''
          }`}
          style={{
            padding: '12px 16px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {getDisplayText()}
        </div>
      </div>

      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDialog-paper': {},
        }}
      >
        <AppBar sx={{ position: 'relative', backgroundColor: background }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Selecciona mes y año
            </Typography>
            {tempSelectedMonth && tempSelectedYear && (
              <Button 
                autoFocus 
                color="inherit"
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
              >
                Siguiente
              </Button>
            )}
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <Typography>Cargando fechas disponibles...</Typography>
            </Box>
          ) : hasNoDates || disabled ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <Typography color="text.secondary">
                No hay fechas disponibles para este destino
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {Object.entries(groupedByYear).map(([year, options]) => (
                <Box key={year}>
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      px: 2,
                      py: 1,
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <Typography variant="h6" sx={{ color: background }} fontWeight="bold">
                      {year}
                    </Typography>
                  </Box>

                  {options.map((option) => {
                    const isSelected =
                      tempSelectedMonth === option.month && tempSelectedYear === option.year;

                    return (
                      <ListItem key={option.value} disablePadding>
                        <ListItemButton
                          onClick={() => handleOptionSelect(option)}
                          selected={isSelected}
                          disabled={disabled}
                          sx={{
                            backgroundColor: isSelected ? background : 'white',
                            color: isSelected ? 'white' : 'text.primary',
                            '&:hover': {
                              backgroundColor: isSelected ? background : 'grey.100',
                            },
                            '&.Mui-selected': {
                              backgroundColor: background,
                              color: 'white',
                              '&:hover': {
                                backgroundColor: background,
                              },
                            },
                            '&.Mui-disabled': {
                              opacity: 0.5,
                              cursor: 'not-allowed',
                            },
                          }}
                        >
                          <ListItemText
                            primary={option.label}
                            primaryTypographyProps={{
                              fontWeight: isSelected ? 'bold' : 'normal',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MonthYearGroupedFullScreenDialog;
